<template>
  <div
    :class="[
      'asc-autocomplete-selector',
      detailRowProp ? 'asc-autocomplete-selector--hasDetails' : null,
    ]"
    :aria-dropdown="label"
    :style="{ minWidth: floatFilter ? 'inherit' : minWidth + 'px' }"
  >
    <template v-if="hasSidebarSlot">
      <div class="asc-autocomplete-selector--sidebar">
        <slot name="sidebar"></slot>
      </div>
    </template>
    <div class="asc-autocomplete-selector--selector">
      <asc-base-selector
        :id="id"
        :autosize="componentType != 'extendedMultiple'"
        :current-text="getCurrentText"
        :dense="dense"
        :disabled="disabled"
        :drop-down-size-matches="dropDownSizeMatches"
        :fixed-position="fixedPosition"
        :float-filter="floatFilter"
        :icon="icon"
        :items="getNonDupedItems"
        :label-below="labelBelow"
        :label="label"
        :multiple="componentType != 'single'"
        :no-label="noLabel"
        :no-placeholder="noPlaceholder"
        :placeholder="placeholder"
        :readonly="readonly"
        :required="required"
        :selected-items="getSelectedItems"
        :on-expand-menu="onExpandMenu"
        :on-collapse-menu="onCollapseMenu"
        :class="getCurrentTextClass"
      >
        <asc-autocomplete-selector-options
          :id="id"
          v-model="model"
          :items="getNonDupedItems"
          :item-height="itemHeight"
          :locked-items="lockedItems"
          :component-type="componentType"
          :float-filter="floatFilter"
          :use-strings="useStrings"
          :no-array="noArray"
          :no-reset="noReset"
          :no-search-input="noSearchInput"
          :display-clear="getDisplayClear"
          :disable-select-all="disableSelectAll"
          :dense="dense"
          :short="short"
          :detail-row-prop="detailRowProp"
          :drop-down-size-matches="dropDownSizeMatches"
          :icon="icon"
          :display-group-name="displayGroupName"
          @input="handleInput"
        ></asc-autocomplete-selector-options>
      </asc-base-selector>
      <template v-if="hasValidationErrorMessage">
        <asc-validation-error
          :value="validationErrorMessage"
          :buttons-enabled="false"
        ></asc-validation-error>
      </template>
      <template v-if="hasFooterSlot">
        <div class="asc-autocomplete-selector--footer">
          <slot name="footer"></slot>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import {
  AscAutocompleteSelectorMixin,
  AscAutocompleteSelectorOptionsMixin,
} from '@/components/mixins'
import AscValidationError from '@/components/atoms/AscValidationError'
import AscBaseSelector from './AscBaseSelector'
import AscAutocompleteSelectorOptions from './AscAutocompleteSelectorOptions'

export default {
  name: 'AscAutocompleteSelector',

  components: {
    AscBaseSelector,
    AscAutocompleteSelectorOptions,
    AscValidationError,
  },

  mixins: [AscAutocompleteSelectorMixin, AscAutocompleteSelectorOptionsMixin],

  props: {
    detailRowProp: {
      type: String,
      default: '',
    },
    minWidth: {
      type: Number,
      default: 50,
    },
    short: {
      type: Boolean,
      default: false,
    },
    disableSelectAll: {
      type: Boolean,
      default: false,
    },
    itemHeight: {
      type: Number,
    },
  },

  data() {
    return {
      viewModel: this.value,
    }
  },

  computed: {
    getNonDupedItems() {
      let nonDupedItems = []
      if (this.items?.length) {
        nonDupedItems = [
          ...new Map(this.items.map(item => [item['text'], item])).values(),
        ]
      }

      if (this.sortItems && nonDupedItems.length) {
        nonDupedItems.sort((a, b) =>
          a.text.toString().toLowerCase() > b.text.toString().toLowerCase()
            ? 1
            : -1,
        )
      }

      return nonDupedItems
    },

    getSelectedItems() {
      if (!this.model) return []
      const valueArray = Array.isArray(this.model) ? this.model : [this.model]
      const results = []
      valueArray.forEach(val => {
        const match = this.getNonDupedItems.find(item => {
          const textValue = this.useStrings ? item.text : item.value
          return textValue === val
        })
        if (match) {
          results.push(match)
        }
      })
      return results
    },

    hasSidebarSlot() {
      return !!this.$slots.sidebar
    },

    hasFooterSlot() {
      return !!this.$slots.footer
    },

    hasValidationErrorMessage() {
      return (
        !this.disabled &&
        !this.readonly &&
        this.displayErrorMessage &&
        this.validationErrorMessage.length > 0 &&
        this.model.length === 0
      )
    },

    model: {
      get() {
        return this.viewModel
      },
      set(newValue) {
        this.viewModel = newValue
      },
    },

    getDisplayClear() {
      return this.displayClear && this.getCurrentText !== this.placeholder
    },

    getCurrentText() {
      const gsis = this.getSelectedItems
      const sis = this.selectedItems

      if (gsis.length > 0) {
        if (this.readonly || this.getNonDupedItems.length === 0) {
          const items = gsis.map(item => {
            return item?.text || ''
          })
          if (this.prefixText.length > 0) {
            return `${this.prefixText} ${items.join(', ')}`
          }
          return `${items.join(', ')}`
        }
      }

      if (sis.length > 0) {
        if (this.readonly || this.getNonDupedItems.length === 0) {
          const items = this.selectedItems.map(item => {
            return item?.text || ''
          })
          if (this.prefixText.length > 0) {
            return `${this.prefixText} ${items.join(', ')}`
          }
          return `${items.join(', ')}`
        }
      }

      if (this.getNonDupedItems.length > 0) {
        let valueArray = []
        if (typeof this.model === 'string') {
          valueArray.push(this.model)
        } else if (typeof this.model === 'number') {
          valueArray.push(this.model)
        } else {
          valueArray = this.model !== null ? [...this.model] : []
        }

        if (valueArray && valueArray.length > 0) {
          const results = []
          const _that = this
          valueArray.forEach(val => {
            const filteredItems = _that.getNonDupedItems.filter(item => {
              return _that.useStrings ? item.text === val : item.value === val
            })
            if (filteredItems.length > 0) {
              results.push(filteredItems[0].text)
            }
          })

          if (this.prefixText.length > 0) {
            return `${this.prefixText} ${results.join(', ')}`
          }

          return `${results.join(', ')}`
        }
      }

      return this.noPlaceholder ? '' : this.placeholder
    },

    getCurrentTextClass() {
      return this.getCurrentText === this.placeholder
        ? 'asc-base-selector--unselected'
        : 'asc-base-selector--selected'
    },
  },

  watch: {
    value(newValue) {
      this.viewModel = newValue
    },
  },

  methods: {
    handleInput(ids, selectedItems) {
      this.$emit('input', this.model)
      if (this.onChange) {
        this.onChange(this.id, this.model, selectedItems)
      }
    },
  },
}
</script>

<style lang="scss">
.asc-base-selector {
  &--unselected {
    .asc-base-selector--current-text {
      color: $gray-500;
    }
  }
  &--selected {
    .asc-base-selector--current-text {
      color: $gray-900;
    }
  }
}

.asc-autocomplete-selector {
  display: flex;

  & .asc-autocomplete-selector--sidebar {
    padding: 6px 8px 8px 0;
  }

  & .asc-autocomplete-selector--selector {
    flex: 1;
    width: 100%;

    & .asc-autocomplete-selector--footer {
      margin-top: 16px;
    }
  }
}
</style>
